/**
 * What needs someone now: one set of checks for the menu's amber counts
 * and the Today page (Design's sketch, Nino approved 12 Oct), so both
 * always agree. Each check says whether it worked: a check that fails is
 * "can't check", never a zero.
 */
import { useQuery } from '@tanstack/react-query';
import { adminOverviewApi, bridgeApiClient, contentStoreApi, requestQueueApi, schedulerApiClient } from '@/lib/api';
import { roomView, sortRooms } from '@/lib/operations';
import type { DrillQueueEntry } from '@/types/command-centre';

export type CheckState = 'loading' | 'ok' | 'error';
export interface Check<T = undefined> { state: CheckState; count: number; detail?: T; retry: () => void }

const every = { refetchInterval: 60_000, retry: false } as const;

const state = (q: { isLoading: boolean; isError: boolean }): CheckState => (q.isError ? 'error' : q.isLoading ? 'loading' : 'ok');

export function useWaitingCounts() {
  const enabled = typeof window !== 'undefined' && !!window.localStorage.getItem('access_token');

  const counters = useQuery({ queryKey: ['sidebar-counters'], queryFn: () => adminOverviewApi.sidebarCounters(), enabled, ...every });
  // Drills learners are waiting on (the Request queue's own list; light).
  const requests = useQuery<DrillQueueEntry[]>({ queryKey: ['cc-request-queue'], queryFn: () => requestQueueApi.list(), enabled, ...every });
  // AI-made items waiting for a person (Review & wording, brief 5).
  const review = useQuery({
    queryKey: ['content-pending-count'],
    queryFn: async () => Number(((await contentStoreApi.pending({ limit: 1 })) as { pending_count?: number }).pending_count ?? 0),
    enabled, ...every,
  });
  // Drafts that need work (Scoring's light count; no badge until the server has it).
  const needsWork = useQuery({ queryKey: ['needs-work-counts'], queryFn: () => adminOverviewApi.needsWorkCounts(), enabled, ...every });
  // Open problems in live rooms (Design's Operations sketch, Nino approved 18 Oct: a red count). Same feed and rules as the Operations page.
  const live = useQuery({ queryKey: ['live-drills'], queryFn: () => bridgeApiClient.getActiveSessions(), enabled, ...every });
  // People waiting for approval.
  const applicants = useQuery({
    queryKey: ['facilitators-pending'],
    queryFn: async () => (((await schedulerApiClient.listFacilitators('pending')) as { facilitators?: { registered_at?: string | null; created_at?: string | null }[] }).facilitators ?? []),
    enabled, ...every,
  });

  const rows = requests.data ?? [];
  const oldestRequest = rows.map((r) => r.oldest_waiting_since).filter((x): x is string => !!x).sort()[0] ?? null;
  const rooms = sortRooms((live.data?.sessions ?? []).map((d) => roomView(d)));
  const firstHot = rooms.find((r) => r.problems.length) ?? null;
  const people = applicants.data ?? [];
  const oldestApplicant = people.map((p) => p.registered_at ?? p.created_at).filter((x): x is string => !!x).sort()[0] ?? null;
  const classes = Number(needsWork.data?.classes?.draft ?? 0);
  const drills = Number(needsWork.data?.skill_drills?.draft ?? 0);
  const reviewN = review.data ?? 0;

  const checks = {
    requests: { state: state(requests), count: rows.length, detail: { learners: rows.reduce((n, r) => n + (r.learners_waiting ?? 0), 0), oldest: oldestRequest }, retry: () => void requests.refetch() } as Check<{ learners: number; oldest: string | null }>,
    facilitators: { state: state(applicants), count: people.length, detail: { oldest: oldestApplicant }, retry: () => void applicants.refetch() } as Check<{ oldest: string | null }>,
    live_drills: {
      state: state(live), count: rooms.reduce((n, r) => n + r.problems.length, 0),
      detail: firstHot ? { title: firstHot.title, problem: firstHot.problems[0].head.endsWith(':') ? `${firstHot.problems[0].head} ${firstHot.problems[0].tail ?? ''}`.trim() : firstHot.problems[0].head } : null,
      retry: () => void live.refetch(),
    } as Check<{ title: string; problem: string } | null>,
    review: { state: state(review), count: reviewN, retry: () => void review.refetch() } as Check,
    // Needs-work drafts: the server may not have the count yet; then only Review's part counts.
    classes: { state: state(needsWork), count: classes, retry: () => void needsWork.refetch() } as Check,
    skill_drills: { state: state(needsWork), count: drills, retry: () => void needsWork.refetch() } as Check,
    incidents: { state: state(counters), count: Number(counters.data?.ops_incidents ?? 0), retry: () => void counters.refetch() } as Check,
  };
  /**
   * "Classes and drills that need work": Review & wording, plus drafts when the server counts them.
   * Since the split (Design's sketch, Nino's go 27 Oct) it is the Class Creator's To do count; the
   * Command Centre's Today shows it as one link line, outside its own total.
   */
  const needWork: Check<{ review: number; classes: number | null; drills: number | null }> = {
    state: review.isError ? 'error' : review.isLoading ? 'loading' : 'ok',
    count: reviewN + (needsWork.isSuccess ? classes + drills : 0),
    detail: { review: reviewN, classes: needsWork.isSuccess ? classes : null, drills: needsWork.isSuccess ? drills : null },
    retry: () => { void review.refetch(); void needsWork.refetch(); },
  };
  const today = [checks.requests, checks.facilitators, checks.live_drills, checks.incidents];
  return { ...checks, needWork, todo: needWork, today: { count: today.reduce((n, c) => n + (c.state === 'ok' ? c.count : 0), 0) } };
}
